import React, { useEffect, useMemo, lazy, Suspense } from "react";

// react-router components
import { Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";

// @mui material components
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";

import { useTranslation } from "react-i18next";

import { themeOptions } from "../assets/theme";

import routes from "../routes";
import LangRouter from "./LangRouter";
import InstallBanner from "../contents/components/InstallBanner";

// Home is loaded eagerly — it's the landing page, must render instantly
import Home from "../contents/pages/home";
const NotFound = lazy(() => import("../contents/pages/NotFound"));
const TermsOfService = lazy(() => import("../contents/legal/terms-of-service"));
const PrivacyPolicy = lazy(() => import("../contents/legal/privacy-policy"));
const BlogList = lazy(() => import("../contents/blog/BlogList"));
const BlogPost = lazy(() => import("../contents/blog/BlogPost"));
const StorePage = lazy(() => import("../contents/stores/StorePage"));

const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "50vh",
    }}
  >
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    <div
      style={{
        width: 32,
        height: 32,
        border: "3px solid #e0e0e0",
        borderTop: "3px solid #1A73E8",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }}
    />
  </div>
);

const ltrCache = createCache({ key: "muiltr" });
const rtlCache = createCache({ key: "muirtl", stylisPlugins: [prefixer, rtlPlugin] });

// Permanent redirect from legacy/typo `/posts/:slug` to canonical
// `/blog/:slug`. Preserves any `:lang` ancestor segment via the parent
// LangRouter (so `/ko/posts/x` → `/ko/blog/x`). `replace` keeps the
// browser history clean for users who land here from a stale link.
function PostsToBlogRedirect() {
  const { lang, slug } = useParams();
  const prefix = lang ? `/${lang}` : "";
  return <Navigate to={`${prefix}/blog/${slug}`} replace />;
}

export default function App() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const isRtl = i18n.language?.split("-")[0] === "ar";

  const theme = useMemo(
    () => createTheme({ ...themeOptions, direction: isRtl ? "rtl" : "ltr" }),
    [isRtl]
  );

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route && route.component) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const renderAppRoutes = (keyPrefix = "") => (
    <>
      {getRoutes(routes)
        .flat(Infinity)
        .filter(Boolean)
        .map((r, i) => React.cloneElement(r, { key: `${keyPrefix}route-${i}` }))}
      <Route key={`${keyPrefix}home`} index element={<Home />} />
      <Route key={`${keyPrefix}tos`} path="legal/terms-of-service" element={<TermsOfService />} />
      <Route key={`${keyPrefix}pp`} path="legal/privacy-policy" element={<PrivacyPolicy />} />
      <Route key={`${keyPrefix}blog`} path="blog" element={<BlogList />} />
      <Route key={`${keyPrefix}blogpost`} path="blog/:slug" element={<BlogPost />} />
      {/* Back-compat: external links / SEO crawlers / typo URLs hitting the
          /posts/:slug pattern get a permanent redirect to the canonical
          /blog/:slug route instead of a confusing 404. */}
      <Route key={`${keyPrefix}postsredir`} path="posts/:slug" element={<PostsToBlogRedirect />} />
      <Route key={`${keyPrefix}store`} path="stores/:slug" element={<StorePage />} />
    </>
  );

  return (
    <CacheProvider value={isRtl ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <InstallBanner />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* English routes (no prefix) */}
            <Route element={<LangRouter />}>{renderAppRoutes("en-")}</Route>

            {/* Localized routes (with /:lang prefix) */}
            <Route path=":lang" element={<LangRouter />}>
              {renderAppRoutes("lang-")}
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    </CacheProvider>
  );
}
